import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Button,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@musa.tech/design-system";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Edit, Users, Plus, Trash2, KeyRound } from "lucide-react";
import type { UserWithPermissions } from "@/types/user";
import { resetUserPassword } from "@/utils/resetPassword";
import { useAnalytics } from "@/hooks/useAnalytics";
import { hashEmail } from "@/lib/analytics";
import { useToast } from "@/hooks/use-toast";
import { useDemoMode } from "@/contexts/DemoModeContext";
import {
  maskEmail,
  maskGeneratorName,
  maskEstablishmentName,
  maskPhoneNumber,
  maskUserName,
} from "@/utils/demoMode";
import useUnifiedAuth, { usePermissions } from "../../hooks/useUnifiedAuth";

interface UserTableProps {
  filteredUsers: UserWithPermissions[];
  showGeneratorColumn: boolean;
  onEditUser: (user: UserWithPermissions) => void;
  onCreateUser: () => void;
  onDeleteUser: (user: UserWithPermissions) => void;
  isLoading: boolean;
}
export const UserTable: React.FC<UserTableProps> = ({
  filteredUsers,
  showGeneratorColumn,
  onEditUser,
  onCreateUser,
  onDeleteUser,
  isLoading,
}) => {
  const { toast } = useToast();
  const { trackButtonClick } = useAnalytics();
  const { isDemoMode } = useDemoMode();
  const { profile } = useUnifiedAuth();
  const { canManageUsers } = usePermissions();
  const [resetPasswordUser, setResetPasswordUser] =
    useState<UserWithPermissions | null>(null);
  const [isResetting, setIsResetting] = useState(false);

  // Pode excluir/resetar senha/editar se for interno, admin legado ou admin em algum generator_link
  const canDeleteUsers = canManageUsers;
  const canEditUsers = canManageUsers;

  const handleResetPassword = async () => {
    if (!resetPasswordUser?.user_id) return;

    setIsResetting(true);
    try {
      await resetUserPassword(resetPasswordUser.user_id, "Musa@1234");
      const targetUserEmailHash = await hashEmail(resetPasswordUser.email);
      trackButtonClick({
        buttonLabel: "usuarios_trocar_senha",
        target_user_id: resetPasswordUser.user_id,
        target_user_email_hash: targetUserEmailHash,
      });
      toast({
        title: "Senha resetada",
        description: `A senha de ${resetPasswordUser.user_name || maskEmail(resetPasswordUser.email, isDemoMode)} foi alterada para Musa@1234.`,
      });
      setResetPasswordUser(null);
    } catch (error) {
      toast({
        title: "Erro ao resetar senha",
        description:
          error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    } finally {
      setIsResetting(false);
    }
  };
  const getRoleBadge = (role: string | null, isInternal: boolean) => {
    if (isInternal) {
      return (
        <Badge variant="outline" className="text-gray-700 border-gray-300">
          Musa Admin
        </Badge>
      );
    }
    switch ((role ?? "").toLowerCase()) {
      case "admin":
        return (
          <Badge variant="outline" className="text-gray-700 border-gray-300">
            Gestor Admin
          </Badge>
        );
      case "standard":
        return (
          <Badge variant="outline" className="text-gray-700 border-gray-300">
            Operacional
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="text-gray-700 border-gray-300">
            {role}
          </Badge>
        );
    }
  };

  const formatEstablishments = (
    establishments: UserWithPermissions["establishments"],
  ) => {
    if (!establishments || establishments.length === 0) {
      return <span className="text-gray-400">Nenhum</span>;
    }
    if (establishments.length === 1) {
      return maskEstablishmentName(
        establishments[0].establishment_name,
        isDemoMode,
      );
    }

    return (
      <div className="space-y-1">
        <span>
          {maskEstablishmentName(
            establishments[0].establishment_name,
            isDemoMode,
          )}
        </span>
        {establishments.length > 1 && (
          <Badge variant="outline" className="ml-2">
            +{establishments.length - 1} mais
          </Badge>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(5)].map((_, i) => (
          <motion.div
            key={i}
            initial={{
              opacity: 0,
            }}
            animate={{
              opacity: 1,
            }}
            transition={{
              delay: i * 0.1,
            }}
            className="h-16 bg-gray-200 rounded animate-pulse"
          />
        ))}
      </div>
    );
  }
  if (filteredUsers.length === 0) {
    return (
      <div className="text-center py-12">
        <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Nenhum usuário encontrado
        </h3>
        <p className="text-gray-500 mb-4">
          Tente ajustar os filtros ou adicione novos usuários.
        </p>
        <Button onClick={onCreateUser}>
          <Plus className="w-4 h-4 mr-2" />
          Adicionar Usuário
        </Button>
      </div>
    );
  }
  return (
    <div className="rounded-md border bg-white w-full overflow-hidden border-neutral-100 shadow-md">
      <div className="overflow-x-auto">
        <Table className="min-w-full">
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead>ID</TableHead>
              <TableHead>Nome</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Telefone</TableHead>
              <TableHead className="text-center">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredUsers.map((user, index) => (
              <motion.tr
                key={user.user_id}
                initial={{
                  opacity: 0,
                  y: 20,
                }}
                animate={{
                  opacity: 1,
                  y: 0,
                }}
                transition={{
                  delay: index * 0.05,
                }}
                className="hover:bg-gray-50/50"
              >
                <TableCell className="text-gray-500 text-sm">{isDemoMode ? '---' : user.user_id}</TableCell>
                <TableCell className="font-medium">
                  {maskUserName(user.user_name, isDemoMode, index)}
                </TableCell>
                <TableCell>{maskEmail(user.email, isDemoMode)}</TableCell>
                <TableCell>{maskPhoneNumber(user.phone_number, isDemoMode) || "-"}</TableCell>
                <TableCell className="text-center">
                  <div className="flex items-center justify-center gap-1">
                    {canEditUsers && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onEditUser(user)}
                        className="h-8 w-8 p-0 text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                    )}
                    {canDeleteUsers && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setResetPasswordUser(user)}
                          className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          title="Resetar senha"
                        >
                          <KeyRound className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDeleteUser(user)}
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </TableCell>
              </motion.tr>
            ))}
          </TableBody>
        </Table>
      </div>

      <AlertDialog
        open={!!resetPasswordUser}
        onOpenChange={(open) => !open && setResetPasswordUser(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Resetar senha do usuário</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja resetar a senha de{" "}
              <strong>
                {resetPasswordUser?.user_name ||
                  maskEmail(resetPasswordUser?.email, isDemoMode)}
              </strong>
              ?
              <br />
              <br />A nova senha será: <strong>Musa@1234</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>

            <Button onClick={handleResetPassword} disabled={isResetting}>
              {isResetting ? "Resetando..." : "Resetar senha"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
